<template>
  <div class="inventory-explorer">
    <!-- Search -->
    <div class="search-container">
      <v-autocomplete
        v-model="searchSelectedUid"
        :items="searchSuggestions"
        item-text="label"
        item-value="uid"
        :label="searchLabel"
        append-icon="mdi-magnify"
        clearable
        dense
        solo
        hide-no-data
        class="mb-3 search-input"
        @change="onSearchSelect"
        @keyup.enter="onSearchEnter"
      >
        <template #item="{ item }">
          <v-list-item-content>
            <v-list-item-title class="search-title">
              {{ item.label }}
            </v-list-item-title>

            <v-list-item-subtitle class="search-subtitle">
              {{ item.typeLabel }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-autocomplete>
    </div>

    <!-- Tree -->
    <div class="tree-container">
      <v-treeview
        :items="treeItems"
        item-key="uid"
        item-text="name"
        :open.sync="openNodes"
        transition
        class="inventory-tree"
      >
        <template #prepend="{ item, open }">
          <v-icon
            :color="isSelected(item) ? 'primary' : undefined"
            class="tree-icon"
            @click.stop="handleNodeLabelClick(item)"
          >
            {{ getIcon(item, open) }}
          </v-icon>
        </template>

        <template #label="{ item }">
          <span
            class="item-label"
            :class="{ 'clicked-folder': isSelected(item) }"
            @click.stop="handleNodeLabelClick(item)"
          >
            {{ displayName(item) }}
          </span>
        </template>
      </v-treeview>
    </div>
  </div>
</template>

<script>
import Dialect from '@/helpers/dialect'

export default {
  name: 'InventoryExplorer',

  props: {
    categories: {
      type: Array,
      default: () => []
    },
    folders: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      Dialect,
      searchSelectedUid: null,

      // Default: show All opened, but keep Available Inventories and My Folders closed
      openNodes: ['main:all'],

      // Prevent tree reset after route/data refresh
      hasInitializedOpenNodes: false,

      selectedNodeUid: 'main:all',

      clickedFolder: {
        uid: 'main:all',
        id: 'all',
        type: 'main',
        name: 'All',
        rawName: 'All',
        clicked: true,
        checked: true
      }
    }
  },

  computed: {
    currentLang() {
      return this.$vuetify?.lang?.current || 'en'
    },

    searchLabel() {
      return (
        this.Dialect['Search folders or categories']?.[this.currentLang] ||
        'Search folders or categories'
      )
    },

    allLabel() {
      return this.Dialect['All']?.[this.currentLang] || 'All'
    },

    availableInventoriesLabel() {
      return (
        this.Dialect['Available Inventories']?.[this.currentLang] ||
        'Available Inventories'
      )
    },

    myFoldersLabel() {
      return this.Dialect['My Folders']?.[this.currentLang] || 'My Folders'
    },

    noCategoryLabel() {
      return this.Dialect['No Category']?.[this.currentLang] || 'No Category'
    },

    treeItems() {
      const categoryChildren = this.buildCategoryTree(
        this.categories,
        0,
        'main:available-inventories'
      )

      const folderChildren = this.buildFolderTree(
        this.folders,
        0,
        'main:my-folders'
      )

      return [
        {
          uid: 'main:all',
          id: 'all',
          name: this.allLabel,
          rawName: 'All',
          type: 'main',
          parentUid: null,
          children: [
            {
              uid: 'main:available-inventories',
              id: 'available-inventories',
              name: this.availableInventoriesLabel,
              rawName: 'Available Inventories',
              type: 'main-category',
              parentUid: 'main:all',
              children: [
                {
                  uid: 'category:no-category',
                  id: 'no_category',
                  name: this.noCategoryLabel,
                  rawName: 'No Category',
                  type: 'category',
                  items: [],
                  parentUid: 'main:available-inventories',
                  children: []
                },
                ...categoryChildren
              ]
            },
            {
              uid: 'main:my-folders',
              id: 'my-folders',
              name: this.myFoldersLabel,
              rawName: 'My Folders',
              type: 'main-folder',
              parentUid: 'main:all',
              children: folderChildren
            }
          ]
        }
      ]
    },

    flatNodes() {
      const result = []

      const walk = (nodes) => {
        nodes.forEach((node) => {
          result.push(node)

          if (node.children && node.children.length) {
            walk(node.children)
          }
        })
      }

      walk(this.treeItems)
      return result
    },

    nodesByUid() {
      return this.flatNodes.reduce((acc, node) => {
        acc[node.uid] = node
        return acc
      }, {})
    },

    searchSuggestions() {
      return this.flatNodes
        .filter((node) =>
          ['category', 'folder', 'main-category', 'main-folder'].includes(
            node.type
          )
        )
        .map((node) => ({
          uid: node.uid,
          label: this.displayName(node),
          type: node.type,
          typeLabel: this.getTypeLabel(node.type)
        }))
    }
  },

  watch: {
    treeItems: {
      immediate: true,
      handler() {
        if (!this.hasInitializedOpenNodes) {
          this.setDefaultOpenNodes()
          this.hasInitializedOpenNodes = true
        }
      }
    }
  },

  mounted() {
    const defaultNode = this.nodesByUid[this.selectedNodeUid]

    if (defaultNode) {
      this.clickedFolder = {
        ...defaultNode,
        clicked: true,
        checked: true
      }
    }
  },

  methods: {
    buildFolderTree(folders, parentId = 0, parentUid = null) {
      return folders
        .filter(
          (folder) => Number(folder.parent_folder_id || 0) === Number(parentId)
        )
        .sort((a, b) => Number(a.id) - Number(b.id))
        .map((folder) => {
          const uid = `folder:${folder.id}`

          return {
            uid,
            id: folder.id,
            name: folder.name,
            rawName: folder.name,
            type: 'folder',
            parentUid,
            original: folder,
            children: this.buildFolderTree(folders, folder.id, uid)
          }
        })
    },

    buildCategoryTree(categories, parentId = 0, parentUid = null) {
      return categories
        .filter(
          (category) =>
            Number(category.parent_category_id || 0) === Number(parentId)
        )
        .sort((a, b) => Number(a.id) - Number(b.id))
        .map((category) => {
          const uid = `category:${category.id}`

          return {
            uid,
            id: category.id,
            name: category.name,
            rawName: category.name,
            type: 'category',
            items: category.items || [],
            parentUid,
            original: category,
            children: this.buildCategoryTree(categories, category.id, uid)
          }
        })
    },

    setDefaultOpenNodes() {
      this.openNodes = ['main:all']
    },

    toggleNode(node) {
      if (!node || !node.uid) return

      if (!node.children || !node.children.length) {
        return
      }

      const open = new Set(this.openNodes)

      if (open.has(node.uid)) {
        open.delete(node.uid)
      } else {
        open.add(node.uid)
      }

      this.openNodes = Array.from(open)
    },

    handleNodeLabelClick(node) {
      if (!node) return

      // Icon and word click both:
      // 1. open/close tree
      // 2. select + emit so parent can route
      this.toggleNode(node)
      this.handleNodeClick(node)
    },

    onSearchSelect(uid) {
      if (!uid) return

      const node = this.nodesByUid[uid]
      if (!node) return

      this.openAncestors(node)
      this.handleNodeClick(node)

      this.$nextTick(() => {
        this.searchSelectedUid = null
      })
    },

    onSearchEnter() {
      if (!this.searchSelectedUid) return

      const node = this.nodesByUid[this.searchSelectedUid]
      if (!node) return

      this.openAncestors(node)
      this.handleNodeClick(node)

      this.$nextTick(() => {
        this.searchSelectedUid = null
      })
    },

    openAncestors(node) {
      const open = new Set(this.openNodes)
      let parentUid = node.parentUid

      while (parentUid) {
        open.add(parentUid)

        const parent = this.nodesByUid[parentUid]
        parentUid = parent?.parentUid || null
      }

      this.openNodes = Array.from(open)
    },

    handleNodeClick(node) {
      if (!node) return

      this.selectedNodeUid = node.uid

      const emittedNode = {
        ...node,
        clicked: true,
        checked: true
      }

      this.clickedFolder = emittedNode
      this.$emit('folder-clicked', emittedNode)
    },

    isSelected(item) {
      return item.uid === this.selectedNodeUid
    },

    displayName(item) {
      if (!item) return ''

      if (item.rawName === 'No Category') {
        return this.noCategoryLabel
      }

      if (item.rawName === 'All') {
        return this.allLabel
      }

      if (item.rawName === 'Available Inventories') {
        return this.availableInventoriesLabel
      }

      if (item.rawName === 'My Folders') {
        return this.myFoldersLabel
      }

      return item.name
    },

    getTypeLabel(type) {
      const labels = {
        category: 'Category',
        folder: 'Folder',
        'main-category': 'Section',
        'main-folder': 'Section',
        main: 'Section'
      }

      return labels[type] || 'Item'
    },

    getIcon(item, open) {
      if (item.type === 'main') {
        return open ? 'mdi-view-dashboard' : 'mdi-view-dashboard-outline'
      }

      if (item.type === 'main-category') {
        return 'mdi-tag-multiple'
      }

      if (item.type === 'main-folder') {
        return open ? 'mdi-folder-open' : 'mdi-folder'
      }

      if (item.type === 'category') {
        return 'mdi-tag-outline'
      }

      if (item.type === 'folder') {
        return open ? 'mdi-folder-open' : 'mdi-folder'
      }

      return 'mdi-file-document-outline'
    }
  }
}
</script>

<style scoped>
.inventory-explorer {
  width: 100%;
  height: 100%;
  max-width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-size: 12px;
  background: #ffffff;
}

.search-container {
  flex-shrink: 0;
  padding: 8px 10px 0 10px;
  background: #ffffff;
  z-index: 2;
}

.search-input {
  font-size: 12px !important;
  margin-bottom: 6px !important;
}

.search-input >>> .v-input__control {
  min-height: 34px !important;
  font-size: 12px !important;
}

.search-input >>> .v-input__slot {
  min-height: 34px !important;
  padding: 0 10px !important;
}

.search-input >>> .v-text-field__details {
  display: none !important;
}

.search-input >>> .v-label {
  font-size: 12px !important;
}

.search-input >>> input {
  font-size: 12px !important;
}

.search-title {
  font-size: 12px;
  font-weight: 500;
}

.search-subtitle {
  font-size: 10px;
  opacity: 0.7;
}

.tree-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 8px 10px 8px;
  background: #ffffff;
  font-size: 12px;
}

.inventory-tree {
  font-size: 12px;
}

/* Compact row */
.tree-container >>> .v-treeview-node__root {
  min-height: 26px !important;
  border-radius: 6px;
  padding-left: 0 !important;
  padding-right: 2px !important;
}

.tree-container >>> .v-treeview-node__root:hover {
  background: rgba(25, 118, 210, 0.04);
}

.tree-container >>> .v-treeview-node--active .v-treeview-node__root {
  background: rgba(25, 118, 210, 0.08);
}

/* Stable indentation per tree level */
.tree-container >>> .v-treeview-node__level {
  width: 18px !important;
  min-width: 18px !important;
  max-width: 18px !important;
  flex: 0 0 18px !important;
}

/* Force every row to reserve the same expand/collapse arrow space */
.tree-container >>> .v-treeview-node__toggle {
  width: 20px !important;
  min-width: 20px !important;
  max-width: 20px !important;
  height: 20px !important;
  flex: 0 0 20px !important;
  margin-right: 0 !important;
}

/* Rows without children keep the space, but the arrow is invisible */
.tree-container >>> .v-treeview-node__toggle--disabled {
  opacity: 0 !important;
  visibility: hidden !important;
  pointer-events: none !important;
}

/* Stable custom icon column */
.tree-container >>> .v-treeview-node__prepend {
  width: 18px !important;
  min-width: 18px !important;
  max-width: 18px !important;
  flex: 0 0 18px !important;
  margin-right: 4px !important;
}

.tree-container >>> .v-treeview-node__content {
  display: flex !important;
  align-items: center !important;
  padding-left: 2px !important;
  font-size: 12px !important;
  min-width: 0;
}

.tree-container >>> .v-icon {
  font-size: 16px !important;
}

.tree-icon {
  transition: color 0.2s ease;
  cursor: pointer;
}

.tree-container >>> .v-treeview-node__label {
  min-width: 0;
  font-size: 12px !important;
  line-height: 1.2 !important;
}

.tree-container::-webkit-scrollbar {
  width: 4px;
}

.tree-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.tree-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.tree-container::-webkit-scrollbar-thumb:hover {
  background: #888;
}

.item-label {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  font-size: 12px !important;
  line-height: 1.2;
  padding: 2px 4px;
  border-radius: 5px;
  transition: all 0.2s ease;
}

.item-label:hover {
  color: #1976d2;
  background-color: rgba(25, 118, 210, 0.05);
}

.clicked-folder {
  color: #1976d2 !important;
  font-weight: 600;
  background-color: rgba(25, 118, 210, 0.1);
}
</style>
